import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { 
  BusinessOpportunity, 
  OpportunitySubscriptionPlan,
  BusinessProposal,
  ProposalItem,
  ProposalSignature,
  BusinessContract,
  ContractSignatory,
  ProposalTemplate,
  ContractTemplate,
  OpportunityStatus,
  HubspotOpportunity,
  HubspotLineItem,
  HubspotQuote
} from '@/types/business-opportunities';
import { v4 as uuidv4 } from 'uuid';
import { HubspotOpportunityWithCustomer, HubspotLineItemWithOpportunity } from '@/types/hubspot';

// Business Opportunities

export const getBusinessOpportunities = async (organizationId?: string) => {
  console.log('Fetching business opportunities');
  const { data, error } = await supabase
    .from('business_opportunities')
    .select(`
      *,
      customer:customers(id, name, logo_url)
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching business opportunities:', error);
    throw error;
  }
  console.log('Fetched business opportunities:', data);
  return data;
};

export const getHubspotOpportunities = async (): Promise<HubspotOpportunityWithCustomer[]> => {
  try {
    console.log("Tentando buscar oportunidades do HubSpot...");
    
    // Limpar o cache para forçar nova busca
    sessionStorage.removeItem('hubspot_opportunities_cache');
    console.log("Cache de oportunidades HubSpot limpo");
    
    // Verificar se já temos dados em cache na sessão
    const cachedData = sessionStorage.getItem('hubspot_opportunities_cache');
    if (cachedData) {
      console.log("Usando dados em cache para oportunidades HubSpot");
      try {
        const opportunitiesWithCustomers = JSON.parse(cachedData);
        return opportunitiesWithCustomers;
      } catch (cacheError) {
        console.error("Erro ao ler cache:", cacheError);
        // Se houver erro no cache, continuar com a busca normal
      }
    }
    
    // Usar XMLHttpRequest para evitar problemas com fetch e CORS
    return new Promise((resolve) => {
      // @ts-ignore - Ignorar erros de tipo aqui
      const supabaseUrl = supabase.supabaseUrl;
      // @ts-ignore - Ignorar erros de tipo aqui
      const supabaseKey = supabase.supabaseKey;
      
      // Primeiro vamos buscar as oportunidades
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${supabaseUrl}/rest/v1/hubspotopportunities?select=*,customer_name,stage_label`, true);
      xhr.setRequestHeader('apikey', supabaseKey);
      xhr.setRequestHeader('Authorization', `Bearer ${supabaseKey}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const hubspotOpportunities = JSON.parse(xhr.responseText) || [];
            console.log(`Encontradas ${hubspotOpportunities.length} oportunidades HubSpot`);
            
            // Array para armazenar os resultados
            const opportunitiesWithCustomers: HubspotOpportunityWithCustomer[] = [];
            
            // Se não encontrou nenhuma oportunidade, retornar array vazio
            if (hubspotOpportunities.length === 0) {
              resolve([]);
              return;
            }
            
            // Imprimir todas as oportunidades para debug
            console.log("Dados brutos das oportunidades:", JSON.stringify(hubspotOpportunities));
            
            // Para cada oportunidade, adicionar o cliente padrão
            for (const opportunity of hubspotOpportunities) {
              // Verificar se o customer_name já existe na oportunidade
              if (!opportunity.customer_name) {
                console.log(`Oportunidade ${opportunity.id} sem customer_name - valor atual:`, opportunity.customer_name);
              } else {
                console.log(`Oportunidade ${opportunity.id} com customer_name: "${opportunity.customer_name}"`);
              }
              
              const customer = {
                id: opportunity.company_id || '',
                name: opportunity.customer_name || 'Empresa sem nome',
                logo_url: null
              };
              
              opportunitiesWithCustomers.push({
                ...opportunity,
                customer,
              });
            }
            
            // Salvar em cache para evitar novas requisições
            try {
              sessionStorage.setItem('hubspot_opportunities_cache', JSON.stringify(opportunitiesWithCustomers));
            } catch (cacheError) {
              console.error("Erro ao salvar cache:", cacheError);
            }
            
            resolve(opportunitiesWithCustomers);
          } catch (parseError) {
            console.error("Erro ao processar resposta:", parseError);
            resolve([]);
          }
        } else {
          console.error(`Erro na requisição: ${xhr.status}`);
          resolve([]);
        }
      };
      
      xhr.onerror = function() {
        console.error("Erro de rede na requisição");
        resolve([]);
      };
      
      xhr.timeout = 15000; // 15 segundos
      xhr.ontimeout = function() {
        console.error("Timeout na requisição");
        resolve([]);
      };
      
      // Enviar a requisição
      xhr.send();
    });
  } catch (error) {
    console.error("Erro em getHubspotOpportunities:", error);
    // Retornar um array vazio para o cliente lidar
    return [];
  }
};

export const getHubspotOpportunityById = async (opportunityId: string): Promise<HubspotOpportunityWithCustomer | null> => {
  try {
    console.log(`Buscando oportunidade HubSpot com ID: ${opportunityId}`);
    
    // Verificar se temos os dados em cache primeiro
    const cachedData = sessionStorage.getItem('hubspot_opportunities_cache');
    if (cachedData) {
      try {
        const opportunities = JSON.parse(cachedData);
        // Procurar a oportunidade no cache
        const cachedOpportunity = opportunities.find((opp: any) => opp.id === opportunityId);
        if (cachedOpportunity) {
          console.log(`Encontrada oportunidade em cache para ID ${opportunityId}`);
          return cachedOpportunity;
        }
      } catch (cacheError) {
        console.error("Erro ao ler cache:", cacheError);
      }
    }
    
    // Se não estava em cache, tentar buscar todas as oportunidades primeiro e depois filtrar
    try {
      const allOpportunities = await getHubspotOpportunities();
      const opportunity = allOpportunities.find(opp => opp.id === opportunityId);
      if (opportunity) {
        return opportunity;
      }
    } catch (fetchError) {
      console.error("Erro ao buscar todas as oportunidades:", fetchError);
    }
    
    // Se chegou aqui, não conseguiu encontrar nos métodos acima
    return null;
  } catch (error) {
    console.error('Erro em getHubspotOpportunityById:', error);
    return null;
  }
};

export const getHubspotLineItemsByDealId = async (dealId: string): Promise<HubspotLineItemWithOpportunity[]> => {
  try {
    console.log(`Fetching HubSpot line items for deal ID: ${dealId}`);
    
    // Clear cache to ensure fresh data
    sessionStorage.removeItem('hubspot_line_items_by_deal_cache_' + dealId);
    
    // Check if we have cached data
    const cachedData = sessionStorage.getItem('hubspot_line_items_by_deal_cache_' + dealId);
    if (cachedData) {
      console.log('Using cached line items data');
      return JSON.parse(cachedData);
    }
    
    // Create a promise that times out after 15 seconds
    const timeoutPromise = new Promise<HubspotLineItemWithOpportunity[]>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timed out after 15 seconds'));
      }, 15000);
    });
    
    // Create the fetch promise
    const fetchLineItemsPromise = new Promise<HubspotLineItemWithOpportunity[]>(async (resolve) => {
      try {
        console.log('Fetching line items from Supabase...');
        
        // Get line items from the hubspotlineitems table where deal_id matches
        // Explicitly include the entity_id field in the select
        const { data: lineItems, error } = await supabase
          .from('hubspotlineitems')
          .select(`
            id, name, description, amount, price, quantity, discount, 
            createdate, hs_acv, hs_arr, hs_cost_of_goods_sold, 
            hs_discount_percentage, hs_line_item_currency_code, 
            hs_mrr, hs_object_id, hs_product_id, hs_recurring_billing_period, 
            hs_recurring_billing_start_date, 
            hs_sku, hs_tcv, hs_term_in_months, 
            recurringbillingfrequency, deal_id, entity_id
          `)
          .eq('deal_id', dealId);
        
        if (error) {
          console.error('Error fetching line items:', error);
          resolve([]);
          return;
        }
        
        console.log(`Found ${lineItems?.length || 0} line items`, lineItems);
        
        // Map the line items with opportunity data
        const lineItemsWithOpportunity = lineItems?.map(item => {
          console.log(`Line item ${item.id} entity_id:`, item.entity_id);
          return {
            ...item,
            opportunity: null
          };
        }) || [];
        
        // Cache the results
        sessionStorage.setItem('hubspot_line_items_by_deal_cache_' + dealId, JSON.stringify(lineItemsWithOpportunity));
        
        resolve(lineItemsWithOpportunity);
      } catch (err) {
        console.error('Error in fetch line items promise:', err);
        resolve([]);
      }
    });
    
    // Race the promises
    const result = await Promise.race([fetchLineItemsPromise, timeoutPromise]);
    return result;
  } catch (error) {
    console.error('Error getting HubSpot line items:', error);
    return [];
  }
};

export const getHubspotQuotesByDealId = async (dealId: string) => {
  console.log(`Fetching Hubspot quotes for deal ID: ${dealId}`);
  try {
    const { data, error } = await supabase
      .from('hubspotquotes' as any)
      .select('*')
      .eq('deal_id', dealId)
      .order('hs_createdate', { ascending: false });

    if (error) {
      console.error('Error fetching Hubspot quotes:', error);
      
      // For missing table errors, return empty array
      if (error.code === '42P01') {
        console.log('The hubspotquotes table does not exist yet. Returning empty array.');
        console.log('Please create the hubspotquotes table to use this feature.');
        return [];
      }
      
      throw error;
    }

    console.log(`Fetched ${data?.length || 0} Hubspot quotes for deal ID ${dealId}`);
    return data;
  } catch (error) {
    console.error(`Error in getHubspotQuotesByDealId for deal ID ${dealId}:`, error);
    return [];
  }
};

export const getBusinessOpportunity = async (opportunityId: string) => {
  console.log(`Fetching business opportunity with ID: ${opportunityId}`);
  
  if (!opportunityId) {
    console.error('Invalid opportunity ID provided (null/undefined/empty)');
    throw new Error('Invalid opportunity ID: No ID was provided');
  }
  
  try {
    console.log(`Making Supabase query for opportunity ID: ${opportunityId}`);
    const { data, error } = await supabase
      .from('business_opportunities')
      .select(`
        *,
        customer:customers(id, name, logo_url, business_identification_number, legal_name, address, city, state, zip),
        opportunity_subscription_plans(
          *,
          subscription_plan:subscription_plans(
            id, 
            name, 
            description,
            billing_frequency,
            subscription_plan_prices(id, price_amount, currency)
          )
        )
      `)
      .eq('id', opportunityId)
      .single();

    if (error) {
      console.error('Supabase error fetching business opportunity:', error);
      if (error.code === 'PGRST116') {
        console.error('Opportunity not found with the provided ID:', opportunityId);
        throw new Error(`Business opportunity with ID ${opportunityId} not found`);
      }
      
      if (error.code === '42P01') {
        console.error('The business_opportunities table does not exist');
        throw new Error('Database schema error: The business opportunities table does not exist');
      }
      
      throw error;
    }
    
    if (!data) {
      console.error('No data returned for business opportunity with ID:', opportunityId);
      throw new Error(`Business opportunity with ID ${opportunityId} not found`);
    }
    
    console.log('Successfully fetched business opportunity data:', data);
    return data;
  } catch (error) {
    console.error('Error in getBusinessOpportunity:', error);
    throw error;
  }
};

export const createBusinessOpportunity = async (opportunity: Partial<BusinessOpportunity>) => {
  // Ensure required fields are present before inserting
  if (!opportunity.customer_id || !opportunity.name || !opportunity.total_value) {
    throw new Error('Missing required fields: customer_id, name, and total_value are required');
  }
  
  const { data, error } = await supabase
    .from('business_opportunities')
    .insert({
      customer_id: opportunity.customer_id,
      name: opportunity.name,
      total_value: opportunity.total_value,
      contact_id: opportunity.contact_id,
      created_at: opportunity.created_at,
      created_by: opportunity.created_by,
      currency_code: opportunity.currency_code,
      description: opportunity.description,
      expected_close_date: opportunity.expected_close_date,
      id: opportunity.id,
      probability: opportunity.probability,
      status: opportunity.status,
      updated_at: opportunity.updated_at,
      updated_by: opportunity.updated_by
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateBusinessOpportunity = async (opportunityId: string, opportunity: Partial<BusinessOpportunity>) => {
  const { data, error } = await supabase
    .from('business_opportunities')
    .update(opportunity)
    .eq('id', opportunityId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteBusinessOpportunity = async (opportunityId: string) => {
  const { error } = await supabase
    .from('business_opportunities')
    .delete()
    .eq('id', opportunityId);

  if (error) throw error;
  return true;
};

// Opportunity Subscription Plans

export const addOpportunitySubscriptionPlan = async (plan: Partial<OpportunitySubscriptionPlan>) => {
  const { data, error } = await supabase
    .from('opportunity_subscription_plans')
    .insert({
      opportunity_id: plan.opportunity_id!,
      subscription_plan_id: plan.subscription_plan_id!,
      total_price: plan.total_price!,
      unit_price: plan.unit_price!,
      quantity: plan.quantity,
      discount_percentage: plan.discount_percentage,
      id: plan.id,
      created_at: plan.created_at,
      updated_at: plan.updated_at
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateOpportunitySubscriptionPlan = async (planId: string, plan: Partial<OpportunitySubscriptionPlan>) => {
  const { data, error } = await supabase
    .from('opportunity_subscription_plans')
    .update(plan)
    .eq('id', planId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteOpportunitySubscriptionPlan = async (planId: string) => {
  const { error } = await supabase
    .from('opportunity_subscription_plans')
    .delete()
    .eq('id', planId);

  if (error) throw error;
  return true;
};

// Business Proposals

export const getBusinessProposals = async (opportunityId: string) => {
  console.log(`Fetching proposals for opportunity ID: ${opportunityId}`);
  
  try {
    // First, try direct fetch with UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (uuidRegex.test(opportunityId)) {
      // Standard UUID format
      const { data, error } = await supabase
        .from('business_proposals')
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('version', { ascending: false });
  
      if (error) {
        console.error('Error fetching proposals by UUID:', error);
        throw error;
      }
      
      return data;
    }
    
    // Check if this is a HubSpot ID by looking for mapping in external_ids
    console.log('Checking external ID mapping for proposals...');
    const { data: mapping, error: mappingError } = await supabase
      .from('external_ids')
      .select('entity_id')
      .eq('external_id', opportunityId)
      .eq('entity_name', 'opportunity')
      .single();
    
    if (mappingError) {
      if (mappingError.code !== 'PGRST116') {
        // If not "no rows returned" error, it's a real error
        console.error('Error checking opportunity mapping:', mappingError);
      }
      
      // For HubSpot IDs without mapping or for errors, try to find any proposals
      // linked directly to the HubSpot ID (if your schema allows this)
      const { data: hubspotProposals, error: hubspotError } = await supabase
        .from('business_proposals')
        .select('*')
        .eq('hubspot_deal_id', opportunityId)
        .order('version', { ascending: false });
      
      if (hubspotError) {
        // If no hubspot_deal_id column exists, this will fail
        console.log('No proposals found with hubspot_deal_id:', hubspotError);
        return []; // Return empty array for HubSpot IDs without mapped proposals
      }
      
      return hubspotProposals;
    }
    
    // We have a mapping, use the internal UUID
    if (mapping && mapping.entity_id) {
      console.log(`Found mapped opportunity ID: ${mapping.entity_id}`);
      const { data, error } = await supabase
        .from('business_proposals')
        .select('*')
        .eq('opportunity_id', mapping.entity_id)
        .order('version', { ascending: false });

      if (error) {
        console.error('Error fetching proposals with mapped ID:', error);
        throw error;
      }
      
      return data;
    }
    
    console.log('No proposals found for the given opportunity ID');
    return []; // Return empty array if no mapping found
  } catch (error) {
    console.error('Error in getBusinessProposals:', error);
    // Don't throw error, just return empty array to avoid breaking the UI
    return [];
  }
};

export const getBusinessProposal = async (proposalId: string) => {
  const { data, error } = await supabase
    .from('business_proposals')
    .select(`
      *,
      opportunity:business_opportunities(
        id, 
        name, 
        customer_id,
        currency_code,
        customer:customers(id, name, business_identification_number, legal_name, address, city, state, zip)
      ),
      proposal_items(*)
    `)
    .eq('id', proposalId)
    .single();

  if (error) throw error;
  return data;
};

export const getBusinessProposalByToken = async (token: string) => {
  const { data, error } = await supabase
    .from('business_proposals')
    .select(`
      *,
      opportunity:business_opportunities(
        id, 
        name, 
        customer_id,
        currency_code,
        customer:customers(id, name, business_identification_number, legal_name, address, city, state, zip)
      ),
      proposal_items(*)
    `)
    .eq('public_token', token)
    .single();

  if (error) throw error;
  return data;
};

export const getHubspotProposalById = async (quoteId: string) => {
  // Primeiro tenta buscar como token de proposta interna
  try {
    return await getBusinessProposalByToken(quoteId);
  } catch (error) {
    console.log('Not a valid token, trying as Hubspot quote ID');
  }

  // Se não encontrar como token, tenta buscar como ID de cotação do Hubspot
  try {
    console.log(`Buscando cotação com ID: ${quoteId}`);
    const { data, error } = await supabase
      .from('hubspotquotes')
      .select('*')
      .eq('id', quoteId)
      .maybeSingle(); // Usar maybeSingle em vez de single para não gerar erro se não encontrar

    if (error) {
      console.error('Error fetching quote data:', error);
      throw error;
    }

    if (!data) {
      console.log(`Cotação com ID ${quoteId} não encontrada`);
      throw new Error(`Quote with ID ${quoteId} not found`);
    }

    console.log(`Cotação encontrada: ${data.id}, buscando dados do negócio...`);

    // Busca os dados da oportunidade
    const { data: dealData, error: dealError } = await supabase
      .from('hubspotopportunities')
      .select(`
        id,
        dealname,
        amount,
        dealstage,
        deal_currency_code,
        customer_name,
        company_id
      `)
      .eq('id', data.deal_id)
      .limit(1); // Limitar a 1 resultado para evitar o erro de múltiplas linhas

    if (dealError) {
      console.error('Error fetching deal data:', dealError);
    }

    const dealInfo = dealData && dealData.length > 0 ? dealData[0] : null;

    console.log(`Dados do negócio: ${dealInfo?.dealname || 'Não encontrado'}`);
    console.log(`Buscando itens para o negócio ID: ${data.deal_id}`);

    // Busca os itens da cotação
    const { data: lineItemsData, error: lineItemsError } = await supabase
      .from('hubspotlineitems')
      .select('*')
      .eq('deal_id', data.deal_id);

    if (lineItemsError) {
      console.error('Error fetching line items:', lineItemsError);
    }

    console.log(`Encontrados ${lineItemsData?.length || 0} itens para a cotação`);

    // Formata os dados para o formato esperado pelo PublicProposalView
    return {
      id: data.id,
      title: `Cotação #${data.hs_quote_number || data.id}`,
      description: data.hs_comments,
      total_value: data.hs_quote_amount || 0,
      currency_code: data.hs_currency || 'BRL',
      status: data.hs_status?.toLowerCase() === 'approved' ? 'accepted' : 
              data.hs_status?.toLowerCase() === 'sent' ? 'sent' :
              data.hs_status?.toLowerCase() === 'draft' ? 'draft' :
              data.hs_status?.toLowerCase() === 'rejected' ? 'rejected' : 'sent',
      valid_until: data.hs_expiration_date,
      pdf_url: data.hs_pdf_download_link,
      opportunity: {
        id: dealInfo?.id || '',
        name: dealInfo?.dealname || '',
        customer_id: dealInfo?.company_id || '',
        currency: dealInfo?.deal_currency_code || 'BRL',
        customer: {
          id: dealInfo?.company_id || '',
          name: dealInfo?.customer_name || 'Cliente',
          business_identification_number: '',
          legal_name: dealInfo?.customer_name || '',
          address: '',
          city: '',
          state: '',
          zip: ''
        }
      },
      proposal_items: Array.isArray(lineItemsData) ? lineItemsData.map(item => ({
        id: item.id,
        description: item.name || item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.price) || 0,
        unit_measure: 'un',
        discount_percentage: parseFloat(item.discount) || 0,
        total_price: parseFloat(item.amount) || 0
      })) : []
    };
  } catch (error) {
    console.error('Error in getHubspotProposalById:', error);
    throw error;
  }
};

export const createBusinessProposal = async (opportunityId: string, proposal: Partial<BusinessProposal>) => {
  // Get the latest version for this opportunity
  const { data: versionData, error: versionError } = await supabase
    .from('business_proposals')
    .select('version')
    .eq('opportunity_id', opportunityId)
    .order('version', { ascending: false })
    .limit(1);

  if (versionError) throw versionError;

  // Increment version by 1 or start at 1 if no versions exist
  const nextVersion = versionData && versionData.length > 0 ? versionData[0].version + 1 : 1;
  const publicToken = uuidv4();

  const { data, error } = await supabase
    .from('business_proposals')
    .insert({ 
      ...proposal, 
      opportunity_id: opportunityId, 
      version: nextVersion,
      public_token: publicToken,
      template_type: proposal.template_type || 'default',
      title: proposal.title || '',
      total_value: proposal.total_value || 0
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateBusinessProposal = async (proposalId: string, proposal: Partial<BusinessProposal>) => {
  const { data, error } = await supabase
    .from('business_proposals')
    .update(proposal)
    .eq('id', proposalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProposalStatus = async (proposalId: string, status: BusinessProposal['status']) => {
  const { data, error } = await supabase
    .from('business_proposals')
    .update({ status })
    .eq('id', proposalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProposalPdfUrl = async (proposalId: string, pdfUrl: string) => {
  const { data, error } = await supabase
    .from('business_proposals')
    .update({ pdf_url: pdfUrl })
    .eq('id', proposalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Proposal Items

export const addProposalItem = async (item: Partial<ProposalItem>) => {
  const { data, error } = await supabase
    .from('proposal_items')
    .insert({
      description: '',
      proposal_id: '',
      quantity: 0,
      total_price: 0,
      unit_measure: '',
      unit_price: 0,
      ...item
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProposalItem = async (itemId: string, item: Partial<ProposalItem>) => {
  const { data, error } = await supabase
    .from('proposal_items')
    .update(item)
    .eq('id', itemId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteProposalItem = async (itemId: string) => {
  const { error } = await supabase
    .from('proposal_items')
    .delete()
    .eq('id', itemId);

  if (error) throw error;
  return true;
};

// Proposal Signatures

export const getProposalSignature = async (proposalId: string) => {
  const { data, error } = await supabase
    .from('proposal_signatures')
    .select('*')
    .eq('proposal_id', proposalId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const createProposalSignature = async (signature: Partial<ProposalSignature>) => {
  // Ensure required fields are present before inserting
  if (!signature.proposal_id || !signature.signed_at || !signature.signer_email || !signature.signer_name) {
    throw new Error('Missing required fields for proposal signature');
  }

  const { data, error } = await supabase
    .from('proposal_signatures')
    .insert({
      proposal_id: signature.proposal_id,
      signed_at: signature.signed_at,
      signer_email: signature.signer_email,
      signer_name: signature.signer_name,
      ...signature
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProposalSignature = async (signatureId: string, signature: Partial<ProposalSignature>) => {
  const { data, error } = await supabase
    .from('proposal_signatures')
    .update(signature)
    .eq('id', signatureId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Business Contracts

export const getBusinessContracts = async (customerId: string) => {
  const { data, error } = await supabase
    .from('business_contracts')
    .select(`
      *,
      proposal:business_proposals(id, title, opportunity_id)
    `)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getAllBusinessContracts = async () => {
  const { data, error } = await supabase
    .from('business_contracts')
    .select(`
      *,
      customer:customers(id, name)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getBusinessContractsByOpportunity = async (opportunityId: string) => {
  // First, get all proposals for this opportunity
  const { data: proposals, error: proposalsError } = await supabase
    .from('business_proposals')
    .select('id')
    .eq('opportunity_id', opportunityId);

  if (proposalsError) throw proposalsError;
  
  if (!proposals || proposals.length === 0) {
    return [];
  }
  
  // Extract proposal IDs
  const proposalIds = proposals.map(p => p.id);
  
  // Now fetch contracts related to these proposals
  const { data: contracts, error: contractsError } = await supabase
    .from('business_contracts')
    .select(`
      *,
      proposal:business_proposals(id, title, opportunity_id, status)
    `)
    .in('proposal_id', proposalIds)
    .order('created_at', { ascending: false });

  if (contractsError) throw contractsError;
  return contracts || [];
};

export const getBusinessContract = async (contractId: string) => {
  const { data, error } = await supabase
    .from('business_contracts')
    .select(`
      *,
      proposal:business_proposals(id, title, opportunity_id, customer_id),
      signatories:contract_signatories(*)
    `)
    .eq('id', contractId)
    .single();

  if (error) throw error;
  return data;
};

export const createContractFromProposal = async (proposalId: string) => {
  // First, get the proposal details
  const { data: proposal, error: proposalError } = await supabase
    .from('business_proposals')
    .select(`
      *,
      opportunity:business_opportunities(customer_id)
    `)
    .eq('id', proposalId)
    .single();

  if (proposalError) throw proposalError;
  
  if (!proposal) {
    throw new Error('Proposal not found');
  }
  
  if (proposal.status !== 'accepted') {
    throw new Error('Only accepted proposals can generate contracts');
  }
  
  // Handle both possible field names for currency
  const currencyCode = 'currency_code' in proposal 
    ? proposal.currency_code 
    : ('currency' in proposal ? proposal.currency : 'BRL');
  
  // Create the contract
  // NOTE: There's a type mismatch between the TypeScript interface (which uses currency_code)
  // and the actual database schema (which uses currency). This should be fixed in a 
  // comprehensive refactoring to ensure consistency.
  const contractData = {
    proposal_id: proposal.id,
    customer_id: proposal.opportunity.customer_id,
    title: `Contrato - ${proposal.title}`,
    description: proposal.description,
    template_type: proposal.template_type,
    status: 'draft',
    start_date: new Date().toISOString().split('T')[0], // Today
    total_value: proposal.total_value,
    currency: currencyCode // Using 'currency' for database, not 'currency_code'
  };
  
  // Type assertion to make TypeScript happy, since there's a mismatch between
  // our TypeScript interface and the actual database schema
  const { data: contract, error: contractError } = await supabase
    .from('business_contracts')
    .insert(contractData as any)
    .select()
    .single();
    
  if (contractError) throw contractError;
  
  return contract;
};

// For backward compatibility with existing code
export const createBusinessContract = async (contract: Partial<BusinessContract>) => {
  // Ensure required fields are present before inserting
  if (!contract.customer_id) {
    throw new Error('customer_id is required for business contracts');
  }
  if (!contract.proposal_id) {
    throw new Error('proposal_id is required for business contracts');
  }
  if (!contract.start_date) {
    throw new Error('start_date is required for business contracts');
  }
  if (!contract.title) {
    throw new Error('title is required for business contracts');
  }
  if (!contract.template_type) {
    throw new Error('template_type is required for business contracts');
  }
  if (contract.total_value === undefined) {
    throw new Error('total_value is required for business contracts');
  }
  
  // NOTE: There's a type mismatch between the TypeScript interface (which uses currency_code)
  // and the actual database schema (which uses currency). This should be fixed in a 
  // comprehensive refactoring to ensure consistency.
  
  // Make a copy of the contract to avoid mutating the input
  const dbContract = { ...contract };
  
  // Handle the currency field conversion if needed
  if ('currency_code' in contract && !('currency' in dbContract)) {
    (dbContract as any).currency = contract.currency_code;
    delete (dbContract as any).currency_code;
  }
  
  // Type assertion to make TypeScript happy, since there's a mismatch between
  // our TypeScript interface and the actual database schema
  const { data, error } = await supabase
    .from('business_contracts')
    .insert(dbContract as any)
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

export const updateBusinessContract = async (contractId: string, updates: Partial<BusinessContract>) => {
  const { data, error } = await supabase
    .from('business_contracts')
    .update(updates)
    .eq('id', contractId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateContractPdfUrl = async (contractId: string, pdfUrl: string) => {
  const { data, error } = await supabase
    .from('business_contracts')
    .update({ pdf_url: pdfUrl })
    .eq('id', contractId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Contract Signatories

export const addContractSignatory = async (signatory: Partial<ContractSignatory>) => {
  // Ensure required fields are present before inserting
  if (!signatory.contract_id) {
    throw new Error('contract_id is required for contract signatories');
  }
  if (!signatory.email) {
    throw new Error('email is required for contract signatories');
  }
  if (!signatory.name) {
    throw new Error('name is required for contract signatories');
  }
  if (!signatory.role) {
    throw new Error('role is required for contract signatories');
  }

  const { data, error } = await supabase
    .from('contract_signatories')
    .insert({
      contract_id: signatory.contract_id,
      email: signatory.email,
      name: signatory.name,
      role: signatory.role,
      company: signatory.company,
      title: signatory.title,
      docusign_envelope_id: (signatory as any).docusign_envelope_id ?? null,
      signature_image_url: (signatory as any).signature_image_url ?? null,
      signature_ip: (signatory as any).signature_ip ?? null,
      signed_at: signatory.signed_at,
      created_at: signatory.created_at,
      updated_at: signatory.updated_at,
      id: signatory.id
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateContractSignatory = async (signatoryId: string, signatory: Partial<ContractSignatory>) => {
  const { data, error } = await supabase
    .from('contract_signatories')
    .update(signatory)
    .eq('id', signatoryId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteContractSignatory = async (signatoryId: string) => {
  const { error } = await supabase
    .from('contract_signatories')
    .delete()
    .eq('id', signatoryId);

  if (error) throw error;
  return true;
};

// Templates

export const getProposalTemplates = async () => {
  const { data, error } = await supabase
    .from('proposal_templates')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return data;
};

export const getProposalTemplate = async (templateId: string) => {
  const { data, error } = await supabase
    .from('proposal_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) throw error;
  return data;
};

export const createProposalTemplate = async (template: Partial<ProposalTemplate>) => {
  // NOTE: There's a type mismatch between the TypeScript interface (which uses html_content)
  // and the actual database schema (which uses template_html). This should be fixed in a 
  // comprehensive refactoring to ensure consistency.
  
  // Create a new object to match the expected database schema
  const dbTemplate = {
    ...(template as any),
    name: template.name || '',
    template_html: template.html_content || '',
    template_type: template.template_type || ''
  };
  
  // Remove the html_content field which isn't expected by the database
  if ('html_content' in dbTemplate) {
    delete dbTemplate.html_content;
  }

  const { data, error } = await supabase
    .from('proposal_templates')
    .insert(dbTemplate as any)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProposalTemplate = async (templateId: string, template: Partial<ProposalTemplate>) => {
  // NOTE: There's a type mismatch between the TypeScript interface (which uses html_content)
  // and the actual database schema (which uses template_html). This should be fixed in a 
  // comprehensive refactoring to ensure consistency.
  
  // Create a new object to match the expected database schema
  const dbTemplate = { ...(template as any) };
  
  // Map html_content to template_html if provided
  if (template.html_content) {
    dbTemplate.template_html = template.html_content;
    delete dbTemplate.html_content;
  }
  
  const { data, error } = await supabase
    .from('proposal_templates')
    .update(dbTemplate as any)
    .eq('id', templateId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getContractTemplates = async () => {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  
  // Map database fields to interface fields
  const mappedData = data.map(template => ({
    ...template,
    html_content: template.template_html || '',
    docx_file_url: template.docx_file_url || '',
    legal_representatives: template.legal_representatives || [],
    witnesses: template.witnesses || [],
  }));
  
  return mappedData;
};

export const getContractTemplate = async (templateId: string) => {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) throw error;
  
  // Map database fields to interface fields
  const mappedData = {
    ...data,
    html_content: data.template_html || '',
    docx_file_url: data.docx_file_url || '',
    legal_representatives: data.legal_representatives || [],
    witnesses: data.witnesses || [],
  };
  
  return mappedData;
};

export const uploadContractTemplateFile = async (file: File, templateId: string) => {
  try {
    // Create a unique filename using the templateId and original file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${templateId}.${fileExt}`;
    const filePath = `templates/${fileName}`;
    
    // Upload the file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('contracts')
      .upload(filePath, file, {
        contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        upsert: true
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get the public URL of the file
    const { data: urlData } = await supabase
      .storage
      .from('contracts')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading template file:', error);
    throw error;
  }
};

export const createContractTemplate = async (template: Partial<ContractTemplate>, file?: File) => {
  try {
    // Create a new object to match the expected database schema
    const dbTemplate = {
      name: template.name || '',
      description: template.description || '',
      template_type: template.template_type || '',
      template_html: template.html_content || '', // Keeping for backward compatibility
      is_active: template.is_active !== undefined ? template.is_active : true,
      legal_representatives: template.legal_representatives || [],
      witnesses: template.witnesses || [],
      // docx_file_url will be added after file upload
    };
    
    // Insert the template record
    const { data, error } = await supabase
      .from('contract_templates')
      .insert(dbTemplate)
      .select()
      .single();

    if (error) throw error;
    
    // If a file was provided, upload it and update the record with the file URL
    if (file) {
      const fileUrl = await uploadContractTemplateFile(file, data.id);
      
      // Update the template record with the file URL
      const { data: updatedData, error: updateError } = await supabase
        .from('contract_templates')
        .update({ docx_file_url: fileUrl })
        .eq('id', data.id)
        .select()
        .single();
        
      if (updateError) throw updateError;
      
      // Map response back to interface format
      const mappedData = {
        ...updatedData,
        html_content: updatedData.template_html || '',
        docx_file_url: updatedData.docx_file_url || '',
        legal_representatives: updatedData.legal_representatives || [],
        witnesses: updatedData.witnesses || [],
      };
      
      return mappedData;
    }
    
    // Map response back to interface format if no file was uploaded
    const mappedData = {
      ...data,
      html_content: data.template_html || '',
      docx_file_url: data.docx_file_url || '',
      legal_representatives: data.legal_representatives || [],
      witnesses: data.witnesses || [],
    };
    
    return mappedData;
  } catch (error) {
    console.error('Error creating contract template:', error);
    throw error;
  }
};

export const updateContractTemplate = async (
  templateId: string, 
  template: Partial<ContractTemplate>,
  file?: File
) => {
  try {
    // Create update object with correct field mapping
    const dbTemplate: any = {};
    
    // Copy allowed fields
    if (template.name !== undefined) dbTemplate.name = template.name;
    if (template.description !== undefined) dbTemplate.description = template.description;
    if (template.template_type !== undefined) dbTemplate.template_type = template.template_type;
    if (template.is_active !== undefined) dbTemplate.is_active = template.is_active;
    if (template.legal_representatives !== undefined) dbTemplate.legal_representatives = template.legal_representatives;
    if (template.witnesses !== undefined) dbTemplate.witnesses = template.witnesses;
    
    // Map html_content to template_html (keeping for backwards compatibility)
    if (template.html_content !== undefined) {
      dbTemplate.template_html = template.html_content;
    }
    
    // Add updated timestamp
    dbTemplate.updated_at = new Date().toISOString();
    
    // If a file was provided, upload it and add the URL to the update object
    if (file) {
      const fileUrl = await uploadContractTemplateFile(file, templateId);
      dbTemplate.docx_file_url = fileUrl;
    }
    
    // Update the template record
    const { data, error } = await supabase
      .from('contract_templates')
      .update(dbTemplate)
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw error;
    
    // Map response back to interface format
    const mappedData = {
      ...data,
      html_content: data.template_html || '',
      docx_file_url: data.docx_file_url || '',
      legal_representatives: data.legal_representatives || [],
      witnesses: data.witnesses || [],
    };
    
    return mappedData;
  } catch (error) {
    console.error('Error updating contract template:', error);
    throw error;
  }
};

/**
 * Special function that can fetch opportunities by both regular UUID IDs and HubSpot IDs
 * Uses the external_ids mapping table when needed
 */
export const getOpportunityByAnyId = async (opportunityId: string) => {
  console.log(`Fetching opportunity with potentially non-UUID ID: ${opportunityId}`);
  
  try {
    // First, try direct fetch if ID looks like UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (uuidRegex.test(opportunityId)) {
      // ID is UUID format, try fetching directly
      console.log(`ID ${opportunityId} looks like UUID, trying direct fetch`);
      try {
        return await getBusinessOpportunity(opportunityId);
      } catch (error) {
        console.log(`Direct fetch failed: ${error.message}, will try alternate methods`);
      }
    }
    
    // ID might be a HubSpot ID, try fetching HubSpot data
    try {
      console.log(`Trying to fetch as HubSpot opportunity with ID: ${opportunityId}`);
      const hubspotData = await getHubspotOpportunityById(opportunityId);
      
      if (hubspotData) {
        console.log(`Found HubSpot opportunity with ID: ${opportunityId}`);
        
        // Convert to compatible format
        const mappedOpportunity = {
          id: hubspotData.id,
          name: hubspotData.dealname,
          description: '',
          total_value: hubspotData.amount || 0,
          currency_code: hubspotData.deal_currency_code || 'BRL',
          status: mapHubspotStageToStatus(hubspotData.dealstage),
          probability: hubspotData.hs_deal_stage_probability || 0,
          expected_close_date: hubspotData.closedate,
          created_at: hubspotData.createdate,
          updated_at: hubspotData.hs_lastmodifieddate,
          
          // Add customer data from hubspotData
          customer: hubspotData.customer || {
            id: hubspotData.company_id || '',
            name: 'Cliente não identificado',
          },
          
          opportunity_subscription_plans: []
        };
        
        return mappedOpportunity;
      }
    } catch (hubspotError) {
      console.log(`HubSpot fetch failed: ${hubspotError.message}`);
    }
    
    // Try with external_ids table (entity_name = 'opportunity')
    console.log(`Trying to find opportunity via external_ids mapping with external_id=${opportunityId}`);
    const { data: externalIdMapping, error: mappingError } = await supabase
      .from('external_ids')
      .select('entity_id')
      .eq('external_id', opportunityId)
      .eq('entity_name', 'opportunity')
      .single();
    
    if (mappingError) {
      console.error('Error finding external ID mapping:', mappingError);
      throw new Error(`Opportunity with ID ${opportunityId} not found in any system`);
    }
    
    if (externalIdMapping && externalIdMapping.entity_id) {
      console.log(`Found mapping to internal ID: ${externalIdMapping.entity_id}`);
      return await getBusinessOpportunity(externalIdMapping.entity_id);
    }
    
    // Nothing worked, throw error
    throw new Error(`Opportunity with ID ${opportunityId} not found in any system`);
  } catch (error) {
    console.error('Error in getOpportunityByAnyId:', error);
    throw error;
  }
};

// Helper function to map HubSpot deal stages to our internal status
export function mapHubspotStageToStatus(dealstage?: string): OpportunityStatus {
  if (!dealstage) return 'open';
  if (dealstage.includes('closedwon')) return 'won';
  if (dealstage.includes('closedlost')) return 'lost';
  if (dealstage.includes('qualifiedtobuy') || 
      dealstage.includes('discovery') ||
      dealstage.includes('presentation') ||
      dealstage.includes('demo') ||
      dealstage.includes('decisionmaker') ||
      dealstage.includes('appointment')) return 'open';
  if (dealstage.includes('proposal') || dealstage.includes('negotiation')) return 'negotiation';
  // Default to open for any unknown stage
  return 'open';
} 